import { useId } from 'react';
import { cn } from './ui';

/**
 * Charts drawn as inline SVG rather than pulled from a library.
 *
 * Two shapes are needed — a ring and a line — and a charting library is around 120 kB for that.
 * Hand-drawn also means they inherit the theme tokens instead of carrying their own palette that
 * would have to be kept in step with light and dark mode.
 */

export function Donut({
  value,
  total,
  label,
  tone = 'primary',
}: {
  value: number;
  total: number;
  label: string;
  tone?: 'primary' | 'success' | 'warning';
}) {
  const fraction = total > 0 ? Math.min(1, value / total) : 0;
  const radius = 34;
  const circumference = 2 * Math.PI * radius;

  const colour =
    tone === 'success'
      ? 'var(--success)'
      : tone === 'warning'
        ? 'var(--warning)'
        : 'var(--primary)';

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="truncate text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">
          {value}
          {total > 0 && <span className="text-muted-foreground">/{total}</span>}
        </p>
      </div>

      <svg
        width="84"
        height="84"
        viewBox="0 0 84 84"
        className="shrink-0"
        role="img"
        aria-label={`${label}: ${value} of ${total}`}
      >
        <circle cx="42" cy="42" r={radius} fill="none" stroke="var(--muted)" strokeWidth="10" />
        <circle
          cx="42"
          cy="42"
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          // Starts at twelve o'clock rather than three, which is how every dial is read.
          transform="rotate(-90 42 42)"
          style={{ transition: 'stroke-dashoffset 400ms ease' }}
        />
      </svg>
    </div>
  );
}

export interface Series {
  label: string;
  colour: string;
  points: number[];
}

/**
 * A line chart over a shared x axis.
 *
 * Rendered in a fixed viewBox and scaled by CSS, so it stays sharp at any width without needing
 * to measure the container or redraw on resize.
 */
export function LineChart({
  series,
  labels,
  height = 200,
  className,
}: {
  series: readonly Series[];
  labels: readonly string[];
  height?: number;
  className?: string;
}) {
  const id = useId();
  const width = 1000;
  const padding = { top: 12, right: 12, bottom: 26, left: 34 };

  const all = series.flatMap((s) => s.points);
  // Never zero: a flat series of zeros would divide by nothing and collapse the chart.
  const max = Math.max(1, ...all);
  const count = Math.max(1, labels.length - 1);

  const x = (index: number) =>
    padding.left + (index / count) * (width - padding.left - padding.right);
  const y = (value: number) =>
    padding.top + (1 - value / max) * (height - padding.top - padding.bottom);

  const gridValues = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('w-full', className)}
      style={{ height }}
      role="img"
      aria-label={series.map((s) => s.label).join(', ')}
    >
      {gridValues.map((value) => (
        <g key={value}>
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={y(value)}
            y2={y(value)}
            stroke="var(--border)"
            strokeWidth="1"
          />
          <text
            x={padding.left - 6}
            y={y(value) + 4}
            textAnchor="end"
            fontSize="11"
            fill="var(--ink-faint, currentColor)"
            opacity="0.6"
          >
            {value}
          </text>
        </g>
      ))}

      {series.map((s) => {
        const points = s.points.map((value, index) => `${x(index)},${y(value)}`).join(' ');
        return (
          <g key={`${id}-${s.label}`}>
            <polyline
              points={points}
              fill="none"
              stroke={s.colour}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}

      {labels.map((label, index) =>
        // Thinned so labels never collide, however many points there are.
        index % Math.ceil(labels.length / 8) === 0 ? (
          <text
            key={label + String(index)}
            x={x(index)}
            y={height - 8}
            textAnchor="middle"
            fontSize="11"
            fill="var(--ink-faint, currentColor)"
            opacity="0.6"
          >
            {label}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/**
 * A used/available ring with the split called out.
 *
 * Separate from Donut, which reads "x of y things". This one is a proportion of a whole and shows
 * both halves, because "26% used" and "74% free" are the same fact told to different people.
 */
export function UsageRing({
  percent,
  colour,
  usedLabel = 'Used',
  freeLabel = 'Available',
}: {
  percent: number;
  colour: string;
  usedLabel?: string;
  freeLabel?: string;
}) {
  const radius = 78;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent));

  return (
    <div className="flex flex-col items-center gap-4">
      <svg
        width="220"
        height="220"
        viewBox="0 0 200 200"
        role="img"
        aria-label={`${clamped}% used`}
      >
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth="34"
          opacity="0.22"
        />
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth="34"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          transform="rotate(-90 100 100)"
          style={{ transition: 'stroke-dashoffset 500ms ease' }}
        />
        <text
          x="100"
          y="106"
          textAnchor="middle"
          fontSize="30"
          fontWeight="600"
          fill="currentColor"
        >
          {clamped}%
        </text>
      </svg>

      <div className="flex gap-5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colour }} />
          {usedLabel} {clamped}%
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colour, opacity: 0.22 }} />
          {freeLabel} {100 - clamped}%
        </span>
      </div>
    </div>
  );
}

export function Legend({ series }: { series: readonly Series[] }) {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
      {series.map((s) => (
        <span key={s.label} className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 rounded" style={{ background: s.colour }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}
